import type { BookLiquiditySeed } from '@/stores/bookLiquiditySettings';
// ── MoneylineSection — drops the canonical value-dashboard GameRows
//    into GameDetail so the row + expandable ladder behavior is
//    pixel-identical instead of a near-clone ─────────────────────────
//
// Reuses `GameRows` exported from ValueDashboard/GameTable.tsx as the
// row renderer. That component already handles:
//   • Click-row-to-expand (no chevron affordance)
//   • Pill-click stops propagation and forwards to onTradeClick
//   • Inline ladder with 3 ASKS → per-venue last-trade strip → 3 BIDS
//   • marketSide-tinted team-name badge when expanded
//   • PosRing position/order chips
//   • American odds + size on every ladder level
//
// Section-local toolbar (Maker/Taker/Market + Yes/No chips) controls
// `marketSide` + `priceMode` for THIS table only — independent from
// the value dashboard's global toolbar. Defaults: Yes/No = NO, price
// mode = user's displaySettings default.
//
// Trade-click → seeds GameDetail's right-column trade ticket via
// onPillClick. We map the canonical GameRows signature
//   (gameKey, teamSide, venue, opts) → TradeTicketContext
// inline.

import { UsMetricHeaders } from '../ValueDashboard/us/UsMarketMetrics';
import { useEffect, useMemo, useState } from 'react';
import { useWorkingView, useWorkingViewField } from '@/hooks/useWorkingView';
import { WorkingViewReset } from '@/components/atoms/WorkingViewReset';
import { moneylineReading } from './readingView';
import { useGameDetailResumesLatest } from './recentViews';
import { useDisplaySettings } from '../../stores/displaySettings';
import { useMarketStore, useOpOddsFor } from '../../stores/marketStore';
import { opHeaderTooltip, opSliceForSport, OP_HEADER_LABEL } from '../../lib/opOdds';
import { useUserKalshiPOS } from '../../hooks/useUserKalshiPOS';
import { useGameStatesPoll } from '../../hooks/useGameStatesPoll';
import { toHydraGameKeyLive } from '../../services/analyticsGameKey';
import { useUsLadderBook } from '../ValueDashboard/us/useUsLadderBook';
import { GameRows, type UsTradeClick } from '../ValueDashboard/GameTable';
import { UsBoardPills, UsCellHoldWidthContext } from '../ValueDashboard/us/UsBoardCell';
import { usVenuePlanForBoardKey } from '@/lib/polymarketUs/gameMapping';
import type { UsBoardQuote } from '@/stores/usBoardQuotes';
import { LT2_GD_ML_CARD_SCOPE } from '../ValueDashboard/lt2Moneyline';
import {
  LIQ_HEADER_TOOLTIP_KALSHI,
  LIQ_HEADER_TOOLTIP_POLY,
  VOL_HEADER_TOOLTIP_KALSHI,
  VOL_HEADER_TOOLTIP_POLY,
} from '../ValueDashboard/sharedBookCue';
import { useMakerRebateActive } from '@/hooks/useMakerRebate';
import { makerRebatePct } from '@/lib/pricing/makerRebate';
import { SectionToolbar } from './SectionToolbar';
import { CompactOutcomeBands, type CompactOutcome } from './CompactOutcomeBands';
import { UsCompactMoneylineBook } from './UsCompactMoneyline';
import { useSharedNow } from '@/lib/infra/sharedClock';
import { EdgeFadeScroller } from '@/components/cards/cardKit';
import { GAME_DETAIL_TABLE_FADE } from './CompactLinesTable';
import { SectionInjuries } from '@/components/organisms/GameInjuries';
import { InjuryLadderPanels, useInjuryPanelsEnabled } from '@/components/organisms/InjuryLadderPanels';
import type { GameRow, MarketSide, TeamSide, TradeTicketContext, VenueBook } from '../ValueDashboard/types';
import type { OddsStreamGameState } from '../../services/oddsStream';

interface Props {
  game: GameRow;
  /** Soccer 3-way: the Draw outcome as a singleSide synth GameRow
   *  (multiOutcomeDrawRow). Renders as a continuation row under the
   *  away/home pair — same layout as the 1H Tie in Game Lines. Absent
   *  (binary sports) the section renders exactly as before. */
  drawGame?: GameRow | null;
  /** Optional deep-link hint from the Positions page. If this ticker
   *  matches one of the four moneyline books, the matching team's row
   *  expands automatically on mount. */
  focusTicker?: string | null;
  /** #1238 — the page's reading focus (`gameDetailFocus`): a US focus visit
   * reads its own record, as a `?ticker` one does. Absent: the ticker. */
  readingFocus?: string | null;
  onPillClick: (ctx: TradeTicketContext) => void;
  /** Read-only View Y/N selection; never sent through the execution callback. */
  onViewSelect?: (ctx: TradeTicketContext) => void;
  /** Live game state from the page's WS odds stream. Passed straight to
   *  GameRows so the moneyline score tracks the header (which reads the
   *  same stream) instead of the laggier analytics-REST store. */
  liveGameState?: OddsStreamGameState | null;
  /** This section is showing the knockout "To Advance" 2-way headline (not the
   *  1X2). Routes the Odds column to the advance consensus (a different market
   *  from the 90-min result it shares a canonicalGameKey with). */
  advanceConsensus?: boolean;
  /** #887 (V4-04) — OPTIONAL, EXPERIMENTAL, and off by default.
   *
   *  `true` draws an expanded outcome as the two-sided book the Game Detail
   *  derivatives already use (#882): the YES and NO ladders of that exact
   *  market side by side, five bids and five asks each, per venue — instead of
   *  the single ladder for whichever side the toolbar / View Y / View N has
   *  selected. Selection still works exactly as it does today; this only
   *  changes what an expanded outcome draws.
   *
   *  No route sets this. It is reached only from the #887 preview
   *  (`scripts/preview-v4-dual-yes.mjs`) while the presentation is evaluated,
   *  and there is deliberately no preference behind it: product acceptance
   *  comes before any activation. */
  dualSideBooks?: boolean;
  /** #1207 — this game's Polymarket US full-game moneyline quote (display
   *  only; the ticket gathers its own evidence). */
  usQuote?: UsBoardQuote | null;
  /** #1207 — opens the shared US ticket. Supplying it shows a US column on
   *  games Polymarket US lists (NFL/CFB/NBA/NHL two-way); every other game,
   *  the soccer Draw and To Advance render exactly as before. */
  onUsPillClick?: UsTradeClick;
}

const VIEW_ID_PREFIX = 'game-detail-moneyline';

/** Expansion target: a pair side, or the Draw continuation row. */
type ExpandedRow = TeamSide | 'draw' | null;

export function MoneylineSection({ game, drawGame = null, focusTicker, readingFocus, onPillClick, onViewSelect, liveGameState, advanceConsensus = false, dualSideBooks = false, usQuote = null, onUsPillClick }: Props) {
  const defaultPriceMode = useDisplaySettings((s) => s.defaultPriceMode);
  const setOrderbookNeeds = useMarketStore((s) => s.setOrderbookNeeds);
  // Section density (#105). Own field rather than sharing Game Lines': this
  // section sits on screen ALONGSIDE the others, and surfaces visible at the
  // same time get independent density (cf. myBotsViewMode / orgBotsViewMode).
  const viewMode = useDisplaySettings((s) => s.moneylineViewMode);
  const setViewMode = useDisplaySettings((s) => s.setMoneylineViewMode);
  // #1398 — opt-in NBA injury panels (display preference, off by default).
  const injuriesOn = useInjuryPanelsEnabled(game.key);
  // #1400 — teams with a drawn panel: the report line leaves their counts to it.
  const [paneledTeams, setPaneledTeams] = useState<readonly string[]>([]);

  // Pre-expand the team whose moneyline ticker matches the URL deep-link
  // ticker (positions page → click). Computed once via initializer so
  // user-driven toggles after mount aren't overridden by re-renders.
  const initialExpandedSide = useMemo<ExpandedRow>(() => {
    if (!focusTicker) return null;
    if (game.kalshi?.away.marketTicker === focusTicker) return 'away';
    if (game.kalshi?.home.marketTicker === focusTicker) return 'home';
    if (game.polymarket?.away.marketTicker === focusTicker) return 'away';
    if (game.polymarket?.home.marketTicker === focusTicker) return 'home';
    if (drawGame?.kalshi?.away.marketTicker === focusTicker) return 'draw';
    if (drawGame?.polymarket?.away.marketTicker === focusTicker) return 'draw';
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // OP (OddsPapi) column — shown only when this game actually has OP
  // coverage and the user toggle is on (architecture §8's auto-hide);
  // hidden ⇒ this section is pixel-identical to pre-feature. Advance
  // headline sections hide it: OP's moneyline is the RESULT market.
  const showOpOdds = useDisplaySettings((s) => s.showOpOdds);
  // A tennis key is shared by both tours — show only this tour's frame.
  const gameOpSlice = opSliceForSport(useOpOddsFor(showOpOdds && !advanceConsensus ? game.key : null), game.sport);
  const hideOpColumn = gameOpSlice == null;
  // Odds filter (Dan 2026-08-23): U defaults OFF everywhere odds render —
  // thead + shared GameRows in lockstep via the same flag.
  const showUnbtdOdds = useDisplaySettings((s) => s.showUnbtdOdds);
  const hideOddsColumn = !showUnbtdOdds;
  // #1207: full-game US moneyline only — never beside a Draw or a To Advance
  // headline, which are different markets from US's two-way game winner.
  const showUs = onUsPillClick != null && drawGame == null && !advanceConsensus
    && usVenuePlanForBoardKey(game.key) !== null;

  // Section-local toolbar state. Defaults: NO + user's default price mode.
  // #1078: only a Recent views return resumes the latest visit.
  const resumeLatest = useGameDetailResumesLatest();
  const reading = useWorkingView(moneylineReading, JSON.stringify([game.key, readingFocus === undefined ? focusTicker ?? null : readingFocus]), {
    marketSide: 'no', priceMode: defaultPriceMode,
  }, { resumeLatest });
  const [marketSide, setMarketSide] = useWorkingViewField(reading, 'marketSide');
  const [priceMode, setPriceMode] = useWorkingViewField(reading, 'priceMode');
  const [expandedRow, setExpandedRow] = useState<ExpandedRow>(initialExpandedSide);
  const expandedTeamSide: TeamSide | null =
    expandedRow === 'away' || expandedRow === 'home' ? expandedRow : null;

  const AWAY_KEY = `${game.key}|away`;
  const HOME_KEY = `${game.key}|home`;
  const DRAW_KEY = drawGame ? `${drawGame.key}|draw` : null;
  const [expandedBandKey, setExpandedBandKey] = useState<string | null>(() =>
    initialExpandedSide === 'away' ? AWAY_KEY : initialExpandedSide === 'home' ? HOME_KEY : initialExpandedSide === 'draw' ? DRAW_KEY : null);
  // One reader for the expanded exact market, whichever density is visible.
  const usExpanded = viewMode === 'detailed' ? expandedTeamSide !== null
    : expandedBandKey === AWAY_KEY || expandedBandKey === HOME_KEY;
  const usLadderBook = useUsLadderBook(showUs && usExpanded ? usQuote?.marketSlug ?? null : null);
  const usNow = useSharedNow(1000, showUs && viewMode === 'compact' && usQuote != null);

  // Kalshi-only ticker list so the user-activity hook can query queue
  // positions for the moneyline tickers. Poly orders flow through a
  // separate relay-poll so they don't need to be in this set.
  const kalshiTickers = useMemo(() => {
    const t: string[] = [];
    if (game.kalshi?.away.marketTicker) t.push(game.kalshi.away.marketTicker);
    if (game.kalshi?.home.marketTicker) t.push(game.kalshi.home.marketTicker);
    if (drawGame?.kalshi?.away.marketTicker) t.push(drawGame.kalshi.away.marketTicker);
    return t;
  }, [game, drawGame]);
  // #666: these sections render HUMAN order chips only, so they read the
  // display lane (bounded per-order retention with provenance) exactly like
  // the Value Dashboard's humanOrdersByTicker. Strict fail-closed consumers
  // (alerts/automation) live elsewhere and keep the strict lane.
  const { positionByTicker, ordersByTicker: strictOrdersByTicker, displayOrdersByTicker } =
    useUserKalshiPOS(kalshiTickers);
  const ordersByTicker = displayOrdersByTicker ?? strictOrdersByTicker;

  // GameDetail isn't behind the dashboard's gameStates poll, so a cold
  // deep-link here left liveGameState null and the Score column fell back to
  // the scheduled start time on an in-progress game. Poll this one game's
  // state so the live/final score (and period status) populate like they do
  // on the dashboard. Writes to the shared gameStatesStore GameRows reads.
  const gameStateKeys = useMemo(() => {
    const k = toHydraGameKeyLive(game.key);
    return k ? [k] : [];
  }, [game.key]);
  useGameStatesPoll(gameStateKeys);

  // Hydra streams depth only for declared tickers. Subscribe to both
  // K + P books for the moneyline so the ladder paints immediately on
  // first click without waiting for an extra subscribe round-trip.
  const allTickers = useMemo<string[]>(() => {
    const out: string[] = [];
    if (game.kalshi?.away.marketTicker) out.push(game.kalshi.away.marketTicker);
    if (game.kalshi?.home.marketTicker) out.push(game.kalshi.home.marketTicker);
    if (game.polymarket?.away.marketTicker) out.push(game.polymarket.away.marketTicker);
    if (game.polymarket?.home.marketTicker) out.push(game.polymarket.home.marketTicker);
    if (drawGame?.kalshi?.away.marketTicker) out.push(drawGame.kalshi.away.marketTicker);
    if (drawGame?.polymarket?.away.marketTicker) out.push(drawGame.polymarket.away.marketTicker);
    return out;
  }, [game, drawGame]);
  useEffect(() => {
    const viewId = `${VIEW_ID_PREFIX}:${game.key}`;
    setOrderbookNeeds(viewId, allTickers);
    return () => {
      setOrderbookNeeds(viewId, []);
    };
  }, [game.key, allTickers, setOrderbookNeeds]);

  const handleTradeClick = (
    gameKey: string,
    teamSide: TeamSide,
    venue: 'kalshi' | 'polymarket',
    opts?: { priceCents?: number; initialOrderSide?: 'yes' | 'no'; postOnly?: boolean; liquiditySeed?: BookLiquiditySeed | null },
  ) => {
    // Draw-row clicks carry the draw synth (teamSide is always 'away' on a
    // singleSide row); the parent maps either game to the right outcome.
    const isDraw = drawGame != null && gameKey === drawGame.key;
    onPillClick({
      game: isDraw ? drawGame : game,
      teamSide: isDraw ? 'away' : teamSide,
      venue,
      initialOrderSide: opts?.initialOrderSide ?? marketSide,
      prefillPrice: opts?.priceCents,
      liquiditySeed: opts?.liquiditySeed,
      postOnly: opts?.postOnly ?? (priceMode === 'maker'),
    });
    // Flip the table's YES/NO toggle to match the pill the trader
    // just clicked — table + panel then read consistently from the
    // toolbar instead of asking for a separate toggle.
    if (opts?.initialOrderSide && opts.initialOrderSide !== marketSide) {
      setMarketSide(opts.initialOrderSide);
    }
  };

  // ── Compact bands (#105) ────────────────────────────────────────────
  // The SAME outcomes the detailed table renders — away, home, and the
  // soccer Draw when present — as a list of named bets. Moneyline has no
  // line dimension, so nothing is collapsed behind a selector: every
  // outcome that renders a row in Detailed renders a band in Compact.
  const makerRebateActive = useMakerRebateActive();
  const bandRebateFraction = makerRebateActive
    ? makerRebatePct({ isLive: game.gameState === 'live' })
    : 0;
  const isFinal = game.gameState === 'final';

  // Built inline rather than memoized: it's three pushes, and the memo's deps
  // would have to include the freshly-built key strings, so it would recompute
  // every render anyway. GameLinesSection builds its bands the same way.
  const compactOutcomes: CompactOutcome[] = (() => {
    const out: CompactOutcome[] = [];
    const push = (
      key: string,
      label: string,
      kalshi: VenueBook | null,
      polymarket: VenueBook | null,
      final: boolean,
      winner: boolean,
    ) => {
      const teamSide: TeamSide = key === HOME_KEY ? 'home' : 'away';
      const teamCode = teamSide === 'home' ? game.homeCode : game.awayCode;
      const native = showUs && key !== DRAW_KEY;
      if (!kalshi && !polymarket && !native) return;
      const onUs = (side: MarketSide, cents: number | null) => onUsPillClick?.(game.key, teamSide,
        { initialOrderSide: side, priceCents: cents ?? undefined });
      out.push({ key, label, kalshi, polymarket, final, winner,
        additionalVenue: native ? {
          prices: <div className="flex items-start gap-1.5" aria-label="Polymarket US quotes">
            <span className="w-12 shrink-0 pt-1 text-[10px] font-semibold text-muted-foreground">POLY US</span>
            <UsCellHoldWidthContext.Provider value><UsBoardPills market={usQuote} now={usNow}
              teamCode={teamCode} teamSide={teamSide} priceMode={priceMode} activeMarketSide={marketSide}
              sideTint isFinal={final} onClick={(_team, side, cents) => onUs(side, cents)} /></UsCellHoldWidthContext.Provider>
          </div>,
          book: <UsCompactMoneylineBook quote={usQuote} book={usLadderBook} now={usNow}
            teamCode={teamCode} teamName={label} side={marketSide} final={final} onTrade={onUs} />,
        } : undefined,
      });
    };
    push(
      AWAY_KEY,
      game.awayName || game.awayCode,
      game.kalshi?.away ?? null,
      game.polymarket?.away ?? null,
      isFinal,
      isFinal && game.finalWinnerSide === 'away',
    );
    push(
      HOME_KEY,
      game.homeName || game.homeCode,
      game.kalshi?.home ?? null,
      game.polymarket?.home ?? null,
      isFinal,
      isFinal && game.finalWinnerSide === 'home',
    );
    if (drawGame && DRAW_KEY) {
      // The draw synth carries its market in the away slot (singleSide row),
      // and it owns its OWN final/winner state: multiOutcomeDrawRow sets
      // finalWinnerSide='away' when the match actually drew. Reading those
      // off the parent game (or hardcoding "not a winner") would print an
      // em-dash on a drawn match where the detailed row prints a ✓.
      // Its label comes from the synth too — the venue may not call it
      // "Draw", and market identity has to survive the density switch.
      const drawFinal = drawGame.gameState === 'final';
      push(
        DRAW_KEY,
        drawGame.awayName || 'Draw',
        drawGame.kalshi?.away ?? null,
        drawGame.polymarket?.away ?? null,
        drawFinal,
        drawFinal && drawGame.finalWinnerSide === 'away',
      );
    }
    return out;
  })();

  // Selection is deliberately independent of the page's one-click adapter.
  // Repeated selection remains idempotent, including while one-click is armed.
  const selectYesSummary = (gameKey: string, teamSide: TeamSide, outcomeSide: MarketSide, venue: 'kalshi' | 'polymarket') => {
    const isDraw = drawGame != null && gameKey === drawGame.key;
    setMarketSide(outcomeSide);
    setExpandedRow(isDraw ? 'draw' : teamSide);
    setExpandedBandKey(isDraw ? DRAW_KEY : teamSide === 'home' ? HOME_KEY : AWAY_KEY);
    // A reading selection follows this exact outcome in the activity rail.
    // No price is supplied: the parent cannot consume it as an armed trade.
    const selectedGame = isDraw ? drawGame : game;
    onViewSelect?.({ game: selectedGame, teamSide: isDraw ? 'away' : teamSide,
      venue, initialOrderSide: outcomeSide, postOnly: priceMode === 'maker' });
  };

  // #887 — the two-sided books name the team on each column: with both sides
  // of both markets open at once, a bare YES/NO no longer says whose contract
  // a price belongs to. Codes on the columns (they sit above a 5-char header),
  // full names in the caption above the pair.
  const dualLabelsFor = (side: TeamSide) => {
    const code = side === 'home' ? game.homeCode : game.awayCode;
    const name = (side === 'home' ? game.homeName : game.awayName) || code;
    return {
      yes: `${code} YES`,
      no: `${code} NO`,
      caption: `${name} — YES and NO, five bids and five asks per book · scroll horizontally if needed`,
    };
  };
  // The Draw synth carries the pair's away CODE, so a code-derived label would
  // read "ARS YES" on the Draw market. Its own name is the only truthful one.
  const drawDualLabels = () => {
    const name = drawGame?.awayName || 'Draw';
    return {
      yes: `${name.toUpperCase()} YES`,
      no: `${name.toUpperCase()} NO`,
      caption: `${name} — YES and NO, five bids and five asks per book · scroll horizontally if needed`,
    };
  };

  const handleBandTrade = (
    outcome: CompactOutcome,
    venue: 'kalshi' | 'polymarket',
    side: MarketSide,
    cents: number | null,
  ) => {
    const gameKey = outcome.key === DRAW_KEY && drawGame ? drawGame.key : game.key;
    const teamSide: TeamSide = outcome.key === HOME_KEY ? 'home' : 'away';
    // Routed through the SAME handler the detailed pills use, with the same
    // postOnly:false the detailed pills pass, so a compact click and a
    // detailed click seed the trade ticket identically.
    handleTradeClick(gameKey, teamSide, venue, {
      priceCents: cents ?? undefined,
      initialOrderSide: side,
      postOnly: false,
    });
  };

  return (
    <section data-view-anchor="moneyline" className="bg-card border-border rounded-lg border">
      <div className="border-border flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
        <h2 className="text-foreground text-lg font-semibold">Moneyline</h2>
        {/* Same trailing cluster as Game Lines and Spread/Total: toolbar,
            hairline, Reset view at the right edge. */}
        <div className="flex shrink-0 items-center gap-2">
          <SectionToolbar
            priceMode={priceMode}
            onPriceMode={setPriceMode}
            marketSide={marketSide}
            onMarketSide={setMarketSide}
            viewMode={viewMode}
            onViewMode={setViewMode}
          />
          <span aria-hidden="true" className="bg-border/60 h-4 w-px" />
          <WorkingViewReset
            label="Moneyline"
            scope="price mode and YES/NO side"
            reset={reading.reset}
            undo={reading.undo}
            canUndo={reading.canUndo}
          />
        </div>
      </div>
      {/* #1398 — the report line: present whenever injuries are on, so the
          ladders below never move when the panels fit or stop fitting. */}
      {injuriesOn && <SectionInjuries gameKey={game.key} teams={[game.awayCode, game.homeCode]} paneled={paneledTeams} />}
      {viewMode === 'compact' ? (
        <>
        <CompactOutcomeBands
          outcomes={compactOutcomes}
          marketSide={marketSide}
          priceMode={priceMode}
          positionByTicker={positionByTicker}
          ordersByTicker={ordersByTicker}
          expandedKey={expandedBandKey}
          onToggleExpand={(key) =>
            setExpandedBandKey((prev) => (prev === key ? null : key))
          }
          onTrade={handleBandTrade}
          dualSideBooks={dualSideBooks}
          onSummaryYesSelect={(outcome, venue) => selectYesSummary(
            outcome.key === DRAW_KEY && drawGame ? drawGame.key : game.key,
            outcome.key === HOME_KEY ? 'home' : 'away', 'yes', venue,
          )}
          onSummaryNoSelect={(outcome, venue) => selectYesSummary(
            outcome.key === DRAW_KEY && drawGame ? drawGame.key : game.key,
            outcome.key === HOME_KEY ? 'home' : 'away', 'no', venue,
          )}
          makerRebatePct={bandRebateFraction}
          emptyMessage="No moneyline markets open yet."
          // GAME-level A/D focus on the moneyline bands: ONE card for the
          // away/home pair (A = the top band's NO, D = the bottom band's NO).
          // The Draw band is deliberately not named — it stays mouse-only and
          // never registers or inherits a card.
          lt2Hotkeys={{
            scope: LT2_GD_ML_CARD_SCOPE,
            gameKey: game.key,
            game,
            awayOutcomeKey: AWAY_KEY,
            homeOutcomeKey: HOME_KEY,
          }}
        />
        </>
      ) : (
      <EdgeFadeScroller className={GAME_DETAIL_TABLE_FADE}>
        <UsCellHoldWidthContext.Provider value>
        <table className="w-full border-collapse border-spacing-0">
          <thead className="bg-muted/50 border-border border-b">
            {/* Column set must match what the shared GameRows emits for
                hideDateColumn (Date dropped, everything else present).
                GameRows split the old Score/Odds cell into separate Score
                and Odds columns (Team | Score | Odds | Kalshi | …); this
                hand-rolled header has to track them or every data cell
                renders under the wrong label. */}
            <tr className="text-muted-foreground text-left text-[10px] font-semibold uppercase tracking-wider">
              <th className="w-48 px-3 py-2">Team</th>
              <th
                className="w-28 px-3 py-2 text-center"
                title="Kickoff time + countdown until the game starts, then the live/final score."
              >
                {game.gameState === 'live' || game.gameState === 'final' ? 'Score' : 'Time'}
              </th>
              {!hideOddsColumn ? (
                <th
                  className="w-16 px-3 py-2 text-center"
                  title="Calculated from unabated market sources"
                >
                  UNBTD
                </th>
              ) : (
                <th className="w-10 px-3 py-2 text-center" title="Your resting orders at each ladder price level">{''}</th>
              )}
              {!hideOpColumn && (
                <th
                  className="w-24 px-3 py-2 text-center"
                  title={opHeaderTooltip()}
                >
                  {OP_HEADER_LABEL}
                </th>
              )}
              <th className="w-32 px-3 py-2 text-center">Kalshi</th>
              <th className="w-16 px-3 py-2 text-right" title={VOL_HEADER_TOOLTIP_KALSHI}>K Vol</th>
              <th className="w-24 px-3 py-2 text-right" title={LIQ_HEADER_TOOLTIP_KALSHI}>K Liq</th>
              <th
                className="w-16 px-3 py-2 text-right"
                title={`Best-bid depth on the active ${marketSide.toUpperCase()} side`}
              >
                K Q
              </th>
              <th
                className="w-16 px-3 py-2 text-right"
                title="Kalshi best-bid depth change over the last 1m on the active side"
              >
                K Δ/m
              </th>
              <th className="w-32 px-3 py-2 text-center">Poly</th>
              <th className="w-16 px-3 py-2 text-right" title={VOL_HEADER_TOOLTIP_POLY}>P Vol</th>
              <th className="w-24 px-3 py-2 text-right" title={LIQ_HEADER_TOOLTIP_POLY}>P Liq</th>
              <th
                className="w-16 px-3 py-2 text-right"
                title="Polymarket best-bid depth change over the last 1m on the active side"
              >
                P Δ/m
              </th>
              {showUs && (<>
                <th
                  className="w-80 px-3 py-2 text-center"
                  title="Polymarket US full-game moneyline: maker shows the best bid, taker the best ask. No last-trade price in this source."
                >
                  US
                </th>
                <UsMetricHeaders compact />
              </>)}
            </tr>
          </thead>
          <tbody>
            <GameRows
              game={game}
              gameIndex={0}
              marketSide={marketSide}
              priceMode={priceMode}
              advanceConsensus={advanceConsensus}
              expandedTeamSide={expandedTeamSide}
              groupSelected={expandedRow === 'draw'}
              groupExtendsBelow={drawGame != null}
              onToggleExpand={(_key, side) =>
                setExpandedRow((prev) => (prev === side ? null : side))
              }
              onTradeClick={handleTradeClick}
              showUsColumn={showUs}
              usMarket={showUs ? usQuote : null}
              usLadderBook={usLadderBook}
              onUsTradeClick={showUs ? onUsPillClick : undefined}
              onSummaryYesSelect={(key, side, venue) => selectYesSummary(key, side, 'yes', venue)}
              onSummaryNoSelect={(key, side, venue) => selectYesSummary(key, side, 'no', venue)}
              ladderLevels={5}
              dualOutcomeLadder={dualSideBooks}
              dualOutcomeLabels={dualLabelsFor}
              positionByTicker={positionByTicker}
              ordersByTicker={ordersByTicker}
              hideDateColumn
              hideOpColumn={hideOpColumn}
              hideOddsColumn={hideOddsColumn}
              hideSelectionBox
              showScores
              gameStateOverride={liveGameState}
              lt2CardScope={LT2_GD_ML_CARD_SCOPE}
              ringSlot="trailing"
            />
            {/* Soccer Draw — continuation row under the pair, same as the 1H
                Tie in Game Lines. oddsOutcome="draw" points the Odds column
                at the 3-way drawFairProbCents and blanks the per-team score
                cell (the synth's away slot holds the DRAW market, so the
                away team's goals would print here otherwise). */}
            {drawGame && (
              <GameRows
                game={drawGame}
                gameIndex={0}
                marketSide={marketSide}
                priceMode={priceMode}
                singleSide="away"
                continuation
                oddsOutcome="draw"
                ringSlot="trailing"
                expandedTeamSide={expandedRow === 'draw' ? 'away' : null}
                groupSelected={expandedTeamSide !== null}
                onToggleExpand={() =>
                  setExpandedRow((prev) => (prev === 'draw' ? null : 'draw'))
                }
                onTradeClick={handleTradeClick}
                onSummaryYesSelect={(key, side, venue) => selectYesSummary(key, side, 'yes', venue)}
              onSummaryNoSelect={(key, side, venue) => selectYesSummary(key, side, 'no', venue)}
              ladderLevels={5}
              dualOutcomeLadder={dualSideBooks}
              dualOutcomeLabels={drawDualLabels}
                positionByTicker={positionByTicker}
                ordersByTicker={ordersByTicker}
                hideDateColumn
                hideOpColumn={hideOpColumn}
                hideOddsColumn={hideOddsColumn}
                hideSelectionBox
                showScores
                gameStateOverride={liveGameState}
                lt2CardScope={LT2_GD_ML_CARD_SCOPE}
              />
            )}
          </tbody>
        </table>
        {/* #1398 — the team panels in the blank strip beside each expanded
            ladder (positioned from the table above; no row or column change).
            Not beside the two-sided books or a Draw: those use the strip. */}
        {injuriesOn && expandedTeamSide !== null && !dualSideBooks && drawGame == null && (
          <InjuryLadderPanels
            gameKey={game.key}
            teams={{
              away: { code: game.awayCode, name: game.awayName },
              home: { code: game.homeCode, name: game.homeName },
            }}
            onPanelTeams={setPaneledTeams}
          />
        )}
        </UsCellHoldWidthContext.Provider>
      </EdgeFadeScroller>
      )}
    </section>
  );
}
